import { useI18n } from "@baton/i18n";
import { cn } from "../../cn.ts";

/**
 * § Feedback and status, ConnectionIndicator: hidden when connected, reconnecting with its
 * attempt, polling ("live updates unavailable"), limited (the instance refused the stream with 429,
 * 2026-10-14: live updates paused and the screen refreshed periodically, worded by which refusal it
 * was), stopped (the stream was closed and is not reconnecting, 2026-10-14), offline. Reads the stream and offline
 * machines' states through the shell. `compact` shows the dot alone for a narrow host such as the
 * rail's 40 px tile, keeping the state's words for assistive technology; the host shows the same
 * words in its tooltip through `connectionText`.
 *
 * Custom in `packages/ui`, neither Spectrum UI nor shadcn/ui having a connection state, and drawn on
 * the utilities of the token theme (2026-09-18, slice (f)): a dot in the state's colour and the words
 * at the caption step. Inside a banner the words and the dot take the banner's size and colour, and the
 * dot stays level with the first line when a long message wraps (2026-10-24).
 */
export type ConnectionState =
  | { readonly kind: "connected" }
  | { readonly kind: "reconnecting"; readonly attempt: number }
  | { readonly kind: "polling" }
  | { readonly kind: "limited"; readonly cause: "stream_limit" | "rate_limit" }
  | { readonly kind: "stopped" }
  | { readonly kind: "offline" };

type Translate = ReturnType<typeof useI18n>["t"];

/** The words for a state that is not connected. */
export function connectionText(state: Exclude<ConnectionState, { readonly kind: "connected" }>, t: Translate): string {
  switch (state.kind) {
    case "reconnecting":
      return t("ui.connection.reconnecting", { attempt: state.attempt });
    case "polling":
      return t("ui.connection.polling");
    case "limited":
      return t("ui.connection.limited", { cause: state.cause });
    case "stopped":
      return t("ui.connection.stopped");
    case "offline":
      return t("ui.connection.offline");
    default: {
      const exhaustive: never = state;
      throw new Error(`unknown connection state ${String(exhaustive)}`);
    }
  }
}

const DOT: Readonly<Record<Exclude<ConnectionState["kind"], "connected">, string>> = {
  reconnecting: "bg-accent-amber-solid",
  polling: "bg-border-strong",
  limited: "bg-border-strong",
  stopped: "bg-border-strong",
  offline: "bg-accent-red-solid",
};

export function ConnectionIndicator({ state, compact = false }: { readonly state: ConnectionState; readonly compact?: boolean }) {
  const { t } = useI18n();
  if (state.kind === "connected") return null;
  return (
    <span data-slot="connection" data-state={state.kind} role="status" className="inline-flex items-center gap-1 text-caption text-text-tertiary in-data-[slot=banner]:flex in-data-[slot=banner]:items-start in-data-[slot=banner]:text-(length:--density-text-size)! in-data-[slot=banner]:leading-(--density-text-line)! in-data-[slot=banner]:text-current!">
      <span data-slot="connection-dot" aria-hidden="true" className={cn("size-(--space-3) flex-none rounded-full outline outline-(length:--border-width) outline-border-strong in-data-[slot=banner]:mt-[calc((var(--density-text-line)_-_var(--space-3))_/_2)] in-data-[slot=banner]:bg-current! in-data-[slot=banner]:outline-current!", DOT[state.kind])} />
      <span className={compact ? "sr-only" : undefined}>{connectionText(state, t)}</span>
    </span>
  );
}
